// app/features/user-profile/components/UserConversationsSectionComponent.tsx
'use client';

import { format, formatDistanceToNow } from 'date-fns';
import { MessageCircle, MessageSquare } from 'lucide-react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import React from 'react';

import { HTFBadge } from '@/app/shared/components/feedback/HTFBadge';

import { UserProfileConversation } from '../user-profile.interface';

interface UserConversationsSectionComponentProps {
  conversations: UserProfileConversation[];
  orgId: string;
}

export function UserConversationsSectionComponent({ 
  conversations, 
  orgId 
}: UserConversationsSectionComponentProps) {
  const router = useRouter();

  if (conversations.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
        <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Conversations</h3>
        <p className="text-gray-500">This user has no conversation history yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          Conversation History ({conversations.length})
        </h3>
      </div>
      
      <div className="divide-y divide-gray-100">
        {conversations.map((conversation) => {
          const isWhatsApp = conversation.channel?.type?.toLowerCase().includes('whatsapp') ||
                             conversation.channel?.name?.toLowerCase().includes('whatsapp');
          
          return (
            <div
              key={conversation.id}
              onClick={() => router.push(`/conversations/${orgId}/${conversation.id}`)}
              className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <div className="flex items-center gap-4">
                {/* Channel Icon */}
                <div className="flex-shrink-0">
                  {isWhatsApp ? (
                    <div className="w-10 h-10 rounded-full bg-[#25D366]/20 flex items-center justify-center">
                      <Image 
                        src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" 
                        alt="WhatsApp"
                        width={20}
                        height={20}
                        className="object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-teal/20 flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-teal-dark" />
                    </div>
                  )}
                </div>
                
                {/* Conversation Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-900">
                      {conversation.channel?.name || 'Unknown Channel'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {conversation.last_message_time 
                        ? format(new Date(conversation.last_message_time), 'MMM d, h:mm a')
                        : 'N/A'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{conversation.message_count} messages</span>
                    <span>•</span>
                    <span>
                      Started {formatDistanceToNow(new Date(conversation.start_time), { addSuffix: true })}
                    </span>
                    {conversation.agent_name && (
                      <>
                        <span>•</span>
                        <span>{conversation.agent_name}</span>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Status Badge */}
                <div className="flex-shrink-0">
                  <HTFBadge 
                    variant={conversation.status === 'active' ? 'success' : 'secondary'} 
                    size="sm" 
                    rounded="full"
                  >
                    {conversation.status}
                  </HTFBadge>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

